if (!customElements.get('bundle-collection-builder')) {
  class BundleCollectionBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.selectedProducts = new Map();
      this.isLoading = false;

      this.tierOne = {
        level: 1,
        code: (this.getAttribute('data-tier-1-code') || '').trim(),
        percent: this.toNumber(this.getAttribute('data-tier-1-percent'))
      };
      this.tierTwo = {
        level: 2,
        code: (this.getAttribute('data-tier-2-code') || '').trim(),
        percent: this.toNumber(this.getAttribute('data-tier-2-percent'))
      };

      this.cards = Array.from(this.querySelectorAll('[data-bcb-product-card]'));
      this.tabs = Array.from(this.querySelectorAll('[data-bcb-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-bcb-panel]'));
      this.selectedList = this.querySelector('[data-bcb-selected-list]');
      this.emptyState = this.querySelector('[data-bcb-empty]');
      this.progress = this.querySelector('[data-bcb-progress]');
      this.progressSteps = Array.from(this.querySelectorAll('[data-bcb-progress-step]'));
      this.freeShippingThreshold = this.toNumber(this.getAttribute('data-free-shipping-threshold-cents')) || 7900;
      this.reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');
      this.quickViewTrigger = this.querySelector('[data-bcb-quick-view-trigger]');
      this.quickViewDrawer = document.getElementById('Product-Drawer');

      this.cards.forEach((card) => this.initializeCard(card));

      this.boundClick = this.handleClick.bind(this);
      this.boundChange = this.handleChange.bind(this);
      this.boundKeydown = this.handleKeydown.bind(this);
      this.boundQuickViewOpen = this.handleQuickViewOpen.bind(this);
      this.addEventListener('click', this.boundClick);
      this.addEventListener('change', this.boundChange);
      this.addEventListener('keydown', this.boundKeydown);
      document.addEventListener('quick-view:open', this.boundQuickViewOpen);

      if (this.quickViewDrawer && 'MutationObserver' in window) {
        this.quickViewDrawerObserver = new MutationObserver(() => {
          const drawerClosed = !this.quickViewDrawer.classList.contains('active');
          const bundleQuickViewActive = this.quickViewDrawer.classList.contains('is-bundle-quick-view');
          if (drawerClosed && bundleQuickViewActive) {
            this.cleanupBundleQuickView();
          }
        });
        this.quickViewDrawerObserver.observe(this.quickViewDrawer, {
          attributes: true,
          attributeFilter: ['class']
        });
      }

      this.updateBundle();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundClick);
      this.removeEventListener('change', this.boundChange);
      this.removeEventListener('keydown', this.boundKeydown);
      document.removeEventListener('quick-view:open', this.boundQuickViewOpen);
      this.quickViewDrawerObserver?.disconnect();
      this.quickViewButtonObserver?.disconnect();
      window.clearTimeout(this.quickViewRequestTimeout);
      this.setCardQuickViewLoading(this.pendingQuickViewCard, false);
      this.pendingQuickViewCard = null;
      this.cleanupBundleQuickView();
      this.initialized = false;
    }

    initializeCard(card) {
      try {
        card.bundleVariants = JSON.parse(card.querySelector('[data-bcb-variants]')?.textContent || '[]');
      } catch (error) {
        card.bundleVariants = [];
      }

      card.bundleButton = card.querySelector('[data-bcb-card-button]');
      card.bundleButtonLabel = card.querySelector('[data-bcb-card-button-label]');
      card.bundleMediaButton = card.querySelector('[data-bcb-card-quick-view]');
      const selectedVariantId = String(card.dataset.selectedVariantId || '');
      card.bundleVariant = card.bundleVariants.find((variant) => String(variant.id) === selectedVariantId)
        || card.bundleVariants.find((variant) => variant.available)
        || card.bundleVariants[0];
      this.initializeCardSwatches(card);
      this.renderCardVariant(card, card.bundleVariant, false);
    }

    handleClick(event) {
      const tab = event.target.closest('[data-bcb-tab]');
      if (tab && this.contains(tab)) {
        this.activateTab(tab);
        return;
      }

      const cardButton = event.target.closest('[data-bcb-card-button]');
      if (cardButton && this.contains(cardButton)) {
        this.handleCardButton(cardButton.closest('[data-bcb-product-card]'));
        return;
      }

      const cardQuickViewButton = event.target.closest('[data-bcb-card-quick-view]');
      if (cardQuickViewButton && this.contains(cardQuickViewButton)) {
        this.openBundleQuickView(cardQuickViewButton.closest('[data-bcb-product-card]'));
        return;
      }

      const removeButton = event.target.closest('[data-bcb-remove]');
      if (removeButton && this.contains(removeButton)) {
        this.removeProduct(removeButton.dataset.productId);
        return;
      }

      const decreaseButton = event.target.closest('[data-bcb-quantity-decrease]');
      if (decreaseButton && this.contains(decreaseButton)) {
        this.changeQuantity(decreaseButton.dataset.productId, -1);
        return;
      }

      const increaseButton = event.target.closest('[data-bcb-quantity-increase]');
      if (increaseButton && this.contains(increaseButton)) {
        this.changeQuantity(increaseButton.dataset.productId, 1);
        return;
      }

      const submitButton = event.target.closest('[data-bcb-submit]');
      if (submitButton && this.contains(submitButton)) {
        this.addBundleToCart();
      }
    }

    handleChange(event) {
      const quantityInput = event.target.closest('[data-bcb-quantity-input]');
      if (quantityInput && this.contains(quantityInput)) {
        this.setQuantity(quantityInput.dataset.productId, quantityInput.value);
      }
    }

    handleKeydown(event) {
      const currentTab = event.target.closest('[data-bcb-tab]');
      if (!currentTab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const currentIndex = this.tabs.indexOf(currentTab);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      const nextTab = this.tabs[nextIndex];
      this.activateTab(nextTab);
      nextTab.focus();
    }

    activateTab(activeTab) {
      const tabId = activeTab.dataset.tabId;

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.tabId === tabId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      if (this.mobileMediaQuery.matches) {
        activeTab.scrollIntoView({
          behavior: this.reducedMotionMediaQuery.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }

    handleCardButton(card) {
      if (!card || this.selectedProducts.has(card.dataset.productId)) return;

      const hasOnlyDefaultVariant = card.dataset.hasOnlyDefaultVariant === 'true';
      if (!hasOnlyDefaultVariant) {
        this.openBundleQuickView(card);
        return;
      }

      this.addVariantToBundle(card, card.bundleVariant);
    }

    addVariantToBundle(card, variant) {
      if (!card || !variant?.available || this.selectedProducts.has(card.dataset.productId)) return;

      card.bundleVariant = variant;
      this.renderCardVariant(card, variant, true);

      this.selectedProducts.set(card.dataset.productId, {
        productId: card.dataset.productId,
        title: card.dataset.productTitle,
        url: card.dataset.productUrl,
        variant: { ...variant },
        quantity: 1
      });

      this.syncProductCards(card.dataset.productId);
      this.updateBundle();
    }

    renderCardVariant(card, variant, updateImage = true) {
      if (!card || !variant) return;

      const isSelected = this.selectedProducts.has(card.dataset.productId);
      const isAvailable = Boolean(variant?.available);
      const price = card.querySelector('[data-bcb-card-price]');
      const comparePrice = card.querySelector('[data-bcb-card-compare-price]');
      const image = card.querySelector('.bundle-collection-card__image');

      if (price && variant) price.innerHTML = this.formatMoney(variant.price);

      if (comparePrice) {
        const showComparePrice = Boolean(variant && variant.compareAtPrice > variant.price);
        comparePrice.hidden = !showComparePrice;
        if (showComparePrice) comparePrice.innerHTML = this.formatMoney(variant.compareAtPrice);
      }

      if (updateImage && image && variant?.image) {
        image.removeAttribute('srcset');
        image.src = variant.image;
        card.bundleOriginalImage = {
          src: variant.image,
          srcset: ''
        };
      }

      if (card.bundleButton) {
        card.bundleButton.disabled = card.bundleButton.classList.contains('loading') || isSelected || !isAvailable;
        this.setCardButtonLabel(card, isAvailable
          ? card.bundleButton.dataset.defaultLabel
          : card.bundleButton.dataset.soldOutLabel);
      }

      if (card.bundleMediaButton) {
        card.bundleMediaButton.disabled = card.bundleMediaButton.classList.contains('loading') || isSelected || !isAvailable;
      }
    }

    setCardButtonLabel(card, label) {
      if (card?.bundleButtonLabel) {
        card.bundleButtonLabel.textContent = label || '';
      } else if (card?.bundleButton) {
        card.bundleButton.textContent = label || '';
      }
    }

    setCardQuickViewLoading(card, isLoading) {
      if (!card) return;

      const isDisabled = isLoading
        || this.selectedProducts.has(card.dataset.productId)
        || !card.bundleVariant?.available;

      if (card.bundleButton) {
        card.bundleButton.classList.toggle('loading', isLoading);
        card.bundleButton.setAttribute('aria-busy', String(isLoading));
        card.bundleButton.disabled = isDisabled;
      }

      if (card.bundleMediaButton) {
        card.bundleMediaButton.classList.toggle('loading', isLoading);
        card.bundleMediaButton.setAttribute('aria-busy', String(isLoading));
        card.bundleMediaButton.disabled = isDisabled;
      }
    }

    initializeCardSwatches(card) {
      const image = card.querySelector('.bundle-collection-card__image');
      const swatches = Array.from(card.querySelectorAll('.product-card-swatch'));
      if (!image || swatches.length === 0) return;

      card.bundleOriginalImage = {
        src: image.getAttribute('src') || '',
        srcset: image.getAttribute('srcset') || ''
      };

      const selectSwatch = (swatch) => {
        swatches.forEach((candidate) => {
          const isActive = candidate === swatch;
          candidate.classList.toggle('active', isActive);
          candidate.setAttribute('aria-pressed', String(isActive));
        });

        const srcset = (swatch.dataset.srcset || '').trim();
        if (srcset) {
          image.setAttribute('srcset', srcset);
        } else if (card.bundleOriginalImage.srcset) {
          image.setAttribute('srcset', card.bundleOriginalImage.srcset);
          image.src = card.bundleOriginalImage.src;
        } else {
          image.removeAttribute('srcset');
          image.src = card.bundleOriginalImage.src;
        }
      };

      swatches.forEach((swatch) => {
        swatch.addEventListener('mouseenter', () => selectSwatch(swatch));
        swatch.addEventListener('focus', () => selectSwatch(swatch));
        swatch.addEventListener('click', () => selectSwatch(swatch));
      });
    }

    openBundleQuickView(card) {
      if (!this.quickViewTrigger || !card?.dataset.productHandle) return;
      if (this.selectedProducts.has(card.dataset.productId) || !card.bundleVariant?.available) return;
      if (this.quickViewTrigger.classList.contains('loading')) return;

      this.setCardQuickViewLoading(card, true);
      this.pendingQuickViewCard = card;
      this.quickViewTrigger.dataset.productHandle = card.dataset.productHandle;
      window.clearTimeout(this.quickViewRequestTimeout);
      this.quickViewRequestTimeout = window.setTimeout(() => {
        this.setCardQuickViewLoading(card, false);
        if (this.pendingQuickViewCard === card) this.pendingQuickViewCard = null;
      }, 15000);
      this.quickViewTrigger.click();
    }

    handleQuickViewOpen(event) {
      const card = this.pendingQuickViewCard;
      if (!card || event.detail?.productHandle !== card.dataset.productHandle) return;

      this.setCardQuickViewLoading(card, false);
      this.pendingQuickViewCard = null;
      window.clearTimeout(this.quickViewRequestTimeout);

      const drawer = this.quickViewDrawer || document.getElementById('Product-Drawer');
      const productForm = drawer?.querySelector('product-form');
      const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');
      if (!drawer || !productForm || !form) return;

      drawer.classList.add('is-bundle-quick-view');
      drawer.querySelectorAll('[data-dynamic-checkout]').forEach((dynamicCheckout) => {
        dynamicCheckout.hidden = true;
      });

      const submitButton = form.querySelector('.single-add-to-cart-button');
      const syncButtonLabel = () => {
        if (!submitButton) return;
        const buttonText = submitButton.querySelector('.single-add-to-cart-button--text');
        const productUnavailable = productForm.classList.contains('product-form--variant-unavailable');
        if (!buttonText || productUnavailable) return;

        const label = card.bundleButton?.dataset.defaultLabel || 'ADD TO BUNDLE';
        if (buttonText.textContent.trim() !== label) buttonText.textContent = label;
      };

      syncButtonLabel();
      this.quickViewButtonObserver?.disconnect();
      if (submitButton && 'MutationObserver' in window) {
        this.quickViewButtonObserver = new MutationObserver(syncButtonLabel);
        this.quickViewButtonObserver.observe(submitButton, {
          attributes: true,
          attributeFilter: ['class', 'disabled'],
          childList: true,
          subtree: true
        });
      }

      form.addEventListener('submit', (submitEvent) => {
        this.handleBundleQuickViewSubmit(submitEvent, card, productForm, form);
      }, true);
    }

    handleBundleQuickViewSubmit(event, card, productForm, form) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!form.reportValidity()) return;
      if (typeof productForm.validateRequiredOptions === 'function'
        && !productForm.validateRequiredOptions(event.submitter)) return;

      const variantId = form.querySelector('[name="id"]')?.value;
      const variant = card.bundleVariants.find((candidate) => String(candidate.id) === String(variantId));
      if (!variant?.available) return;

      this.addVariantToBundle(card, variant);
      this.closeBundleQuickView(card.bundleButton);
    }

    closeBundleQuickView(focusTarget) {
      const drawer = this.quickViewDrawer || document.getElementById('Product-Drawer');
      document.body.classList.remove('open-cc--product');
      drawer?.classList.remove('active');
      this.cleanupBundleQuickView();

      window.setTimeout(() => {
        const drawerContent = drawer?.querySelector('#Product-Drawer-Content');
        if (drawerContent) drawerContent.innerHTML = '';
        focusTarget?.focus();
      }, 260);
    }

    cleanupBundleQuickView() {
      this.quickViewButtonObserver?.disconnect();
      this.quickViewButtonObserver = null;
      if (this.quickViewDrawer?.classList.contains('is-bundle-quick-view')) {
        this.quickViewDrawer.classList.remove('is-bundle-quick-view');
      }
    }

    syncProductCards(productId) {
      const isSelected = this.selectedProducts.has(String(productId));

      this.cards
        .filter((card) => card.dataset.productId === String(productId))
        .forEach((card) => {
          card.classList.toggle('is-selected', isSelected);

          if (card.bundleButton) {
            card.bundleButton.disabled = card.bundleButton.classList.contains('loading')
              || isSelected
              || !card.bundleVariant?.available;
            this.setCardButtonLabel(card, card.bundleVariant?.available
              ? card.bundleButton.dataset.defaultLabel
              : card.bundleButton.dataset.soldOutLabel);
          }

          if (card.bundleMediaButton) {
            card.bundleMediaButton.disabled = card.bundleMediaButton.classList.contains('loading')
              || isSelected
              || !card.bundleVariant?.available;
          }
        });
    }

    removeProduct(productId) {
      if (!this.selectedProducts.delete(String(productId))) return;
      this.syncProductCards(productId);
      this.updateBundle();
    }

    changeQuantity(productId, delta) {
      const item = this.selectedProducts.get(String(productId));
      if (!item) return;
      this.setQuantity(productId, item.quantity + delta);
    }

    setQuantity(productId, rawQuantity) {
      const item = this.selectedProducts.get(String(productId));
      if (!item) return;

      const parsedQuantity = Number.parseInt(rawQuantity, 10);
      const inventoryMax = this.toNumber(item.variant.inventoryMax);
      const maximum = inventoryMax > 0 ? inventoryMax : 999;
      item.quantity = Math.min(maximum, Math.max(1, Number.isFinite(parsedQuantity) ? parsedQuantity : 1));
      this.updateBundle();
    }

    updateBundle() {
      this.renderSelectedProducts();
      this.updateProgressAndTotals();
      this.showError('');
    }

    renderSelectedProducts() {
      if (!this.selectedList || !this.emptyState) return;

      this.selectedList.innerHTML = '';
      this.emptyState.hidden = this.selectedProducts.size > 0;

      this.selectedProducts.forEach((item) => {
        const listItem = document.createElement('li');
        listItem.className = 'bundle-collection-builder__selected-item';
        listItem.dataset.productId = item.productId;

        const imageMarkup = item.variant.image
          ? `<img class="bundle-collection-builder__selected-image" src="${this.escapeAttribute(item.variant.image)}" alt="">`
          : '<span class="bundle-collection-builder__selected-image"></span>';
        const variantTitle = item.variant.title && item.variant.title !== 'Default Title'
          ? `<span class="bundle-collection-builder__selected-variant">${this.escapeHtml(item.variant.title)}</span>`
          : '';
        const maxAttribute = this.toNumber(item.variant.inventoryMax) > 0
          ? ` max="${this.toNumber(item.variant.inventoryMax)}"`
          : '';

        listItem.innerHTML = `
          ${imageMarkup}
          <div class="bundle-collection-builder__selected-info">
            <p class="bundle-collection-builder__selected-title">${this.escapeHtml(item.title)}</p>
            ${variantTitle}
            <span class="bundle-collection-builder__selected-price">${this.formatMoney(item.variant.price)}</span>
          </div>
          <div class="bundle-collection-builder__selected-actions">
            <div class="bundle-collection-builder__quantity">
              <button type="button" data-bcb-quantity-decrease data-product-id="${item.productId}" aria-label="${this.escapeAttribute(this.dataset.decreaseLabel)}">−</button>
              <input type="number" value="${item.quantity}" min="1"${maxAttribute} inputmode="numeric" data-bcb-quantity-input data-product-id="${item.productId}" aria-label="Quantity">
              <button type="button" data-bcb-quantity-increase data-product-id="${item.productId}" aria-label="${this.escapeAttribute(this.dataset.increaseLabel)}">+</button>
            </div>
            <button type="button" class="bundle-collection-builder__remove" data-bcb-remove data-product-id="${item.productId}">${this.escapeHtml(this.dataset.removeLabel)}</button>
          </div>
        `;

        this.selectedList.appendChild(listItem);
      });
    }

    updateProgressAndTotals() {
      const quantity = this.totalQuantity();
      const tier = this.activeTier(quantity);
      const subtotal = Array.from(this.selectedProducts.values()).reduce(
        (sum, item) => sum + (item.variant.price * item.quantity),
        0
      );
      const savings = tier
        ? Array.from(this.selectedProducts.values()).reduce(
          (sum, item) => sum + Math.floor((item.variant.price * item.quantity * tier.percent) / 100),
          0
        )
        : 0;
      const total = Math.max(0, subtotal - savings);

      this.updateProgress(quantity, total);

      this.querySelectorAll('[data-bcb-total]').forEach((element) => {
        element.innerHTML = this.formatMoney(total);
      });
      this.querySelectorAll('[data-bcb-savings]').forEach((element) => {
        element.hidden = savings <= 0;
      });
      this.querySelectorAll('[data-bcb-savings-value]').forEach((element) => {
        element.innerHTML = this.formatMoney(savings);
      });

      this.querySelectorAll('[data-bcb-submit]').forEach((button) => {
        button.disabled = quantity === 0 || this.isLoading;
      });
    }

    updateProgress(quantity, total) {
      if (!this.progress || this.progressSteps.length === 0) return;

      const freeShippingReached = total >= this.freeShippingThreshold;
      const activeStates = {
        start: quantity >= 1,
        'free-shipping': freeShippingReached,
        'tier-1': quantity >= 2,
        'tier-2': quantity >= 3
      };

      this.progressSteps.forEach((step) => {
        step.classList.toggle('is-active', Boolean(activeStates[step.dataset.progressType]));
      });

      let progressOrder = ['start', 'free-shipping', 'tier-1', 'tier-2'];
      if (!freeShippingReached && quantity >= 3) {
        progressOrder = ['start', 'tier-1', 'tier-2', 'free-shipping'];
      } else if (!freeShippingReached && quantity >= 2) {
        progressOrder = ['start', 'tier-1', 'free-shipping', 'tier-2'];
      }

      this.reorderProgressSteps(progressOrder);
    }

    reorderProgressSteps(progressOrder) {
      const stepsByType = new Map(
        this.progressSteps.map((step) => [step.dataset.progressType, step])
      );
      const orderedSteps = progressOrder.map((type) => stepsByType.get(type)).filter(Boolean);
      const orderChanged = orderedSteps.some((step, index) => this.progress.children[index] !== step);
      if (!orderChanged) return;

      this.progressSteps.forEach((step) => {
        step.getAnimations?.().forEach((animation) => animation.cancel());
      });

      const previousPositions = new Map(
        this.progressSteps.map((step) => [step, step.getBoundingClientRect()])
      );

      orderedSteps.forEach((step) => this.progress.appendChild(step));

      if (this.reducedMotionMediaQuery.matches || typeof Element.prototype.animate !== 'function') return;

      orderedSteps.forEach((step) => {
        const previousPosition = previousPositions.get(step);
        const nextPosition = step.getBoundingClientRect();
        const offsetX = previousPosition.left - nextPosition.left;
        const offsetY = previousPosition.top - nextPosition.top;
        if (offsetX === 0 && offsetY === 0) return;

        step.animate(
          [
            { transform: `translate(${offsetX}px, ${offsetY}px)` },
            { transform: 'translate(0, 0)' }
          ],
          {
            duration: 360,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
          }
        );
      });
    }

    totalQuantity() {
      return Array.from(this.selectedProducts.values()).reduce((sum, item) => sum + item.quantity, 0);
    }

    activeTier(quantity = this.totalQuantity()) {
      if (quantity >= 3) return this.tierTwo;
      if (quantity >= 2) return this.tierOne;
      return null;
    }

    async addBundleToCart() {
      if (this.isLoading || this.selectedProducts.size === 0) return;

      const tier = this.activeTier();
      if (tier && (!tier.code || tier.percent <= 0)) {
        this.showError(this.dataset.discountErrorLabel);
        return;
      }

      this.setLoading(true);
      this.showError('');

      let addedItems = [];
      let previousCodes = [];

      try {
        const cartBeforeAdd = await this.fetchCart();
        previousCodes = this.extractDiscountCodes(cartBeforeAdd);
        const desiredCodes = this.desiredDiscountCodes(previousCodes, tier);
        const activeBundleCode = desiredCodes.find((code) => this.isBundleCode(code));
        const bundleKey = `bundle-${this.dataset.sectionId}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const items = Array.from(this.selectedProducts.values()).map((item) => ({
          id: Number(item.variant.id),
          quantity: item.quantity,
          properties: {
            _bundle_key: bundleKey,
            _bundle_section: this.dataset.sectionId,
            _bundle_tier: tier ? String(tier.level) : '0'
          }
        }));

        const addResponse = await fetch(this.cartEndpoint('add.js'), {
          method: 'POST',
          headers: this.jsonHeaders(),
          body: JSON.stringify({
            items,
            sections: this.getSectionsToRender().map((section) => section.section),
            sections_url: window.location.pathname
          })
        });
        const addResult = await addResponse.json();

        if (!addResponse.ok || addResult.status) {
          throw new Error(addResult.description || addResult.message || this.dataset.cartErrorLabel);
        }

        addedItems = addResult.items || [];
        let cartState = addResult;

        if (activeBundleCode) {
          cartState = await this.updateCartDiscounts(desiredCodes);
          if (!this.discountIsApplicable(cartState, activeBundleCode)) {
            throw new Error(this.dataset.discountErrorLabel);
          }
        }

        this.renderCartSections(cartState);
        this.clearSelection();

        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart:item-added', {
            product: addedItems[0] || addResult
          });
        }

        this.openCartDrawer();
      } catch (error) {
        if (addedItems.length > 0) {
          try {
            const rollbackState = await this.rollbackAddedItems(addedItems, previousCodes);
            this.renderCartSections(rollbackState);
          } catch (rollbackError) {
            console.error('Bundle rollback failed:', rollbackError);
          }
        }

        this.showError(error.message || this.dataset.cartErrorLabel);
      } finally {
        this.setLoading(false);
      }
    }

    async fetchCart() {
      const response = await fetch(this.cartEndpoint('cart.js'), {
        method: 'GET',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.dataset.cartErrorLabel);
      return result;
    }

    async updateCartDiscounts(codes) {
      const response = await fetch(this.cartEndpoint('update.js'), {
        method: 'POST',
        headers: this.jsonHeaders(),
        body: JSON.stringify({
          discount: codes.join(','),
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        })
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.dataset.discountErrorLabel);
      }
      return result;
    }

    async rollbackAddedItems(addedItems, previousCodes) {
      const updates = {};
      addedItems.forEach((item) => {
        if (item.key) updates[item.key] = 0;
      });

      const response = await fetch(this.cartEndpoint('update.js'), {
        method: 'POST',
        headers: this.jsonHeaders(),
        body: JSON.stringify({
          updates,
          discount: previousCodes.join(','),
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.dataset.cartErrorLabel);
      return result;
    }

    desiredDiscountCodes(existingCodes, tier) {
      const uniqueCodes = this.uniqueCodes(existingCodes);
      const otherCodes = uniqueCodes.filter((code) => !this.isBundleCode(code));
      if (tier?.code) otherCodes.push(tier.code);
      return this.uniqueCodes(otherCodes);
    }

    extractDiscountCodes(cart) {
      const codes = [];

      (cart.discount_codes || []).forEach((discount) => {
        if (discount?.code) codes.push(discount.code);
      });

      (cart.cart_level_discount_applications || []).forEach((discount) => {
        if (discount?.type === 'discount_code' && discount.title) codes.push(discount.title);
      });

      (cart.items || []).forEach((item) => {
        (item.line_level_discount_allocations || []).forEach((allocation) => {
          const discount = allocation.discount_application;
          if (discount?.type === 'discount_code' && discount.title) codes.push(discount.title);
        });
      });

      return this.uniqueCodes(codes);
    }

    discountIsApplicable(cart, code) {
      const discountCode = (cart.discount_codes || []).find((discount) => this.sameCode(discount.code, code));
      if (discountCode) return discountCode.applicable !== false;
      return this.extractDiscountCodes(cart).some((appliedCode) => this.sameCode(appliedCode, code));
    }

    uniqueCodes(codes) {
      const seen = new Set();
      return (codes || []).filter((code) => {
        const normalized = String(code || '').trim().toLowerCase();
        if (!normalized || seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
      }).map((code) => String(code).trim());
    }

    isBundleCode(code) {
      return this.sameCode(code, this.tierOne.code) || this.sameCode(code, this.tierTwo.code);
    }

    sameCode(firstCode, secondCode) {
      return Boolean(firstCode && secondCode)
        && String(firstCode).trim().toLowerCase() === String(secondCode).trim().toLowerCase();
    }

    clearSelection() {
      const productIds = Array.from(this.selectedProducts.keys());
      this.selectedProducts.clear();
      productIds.forEach((productId) => this.syncProductCards(productId));
      this.updateBundle();
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.querySelectorAll('[data-bcb-submit]').forEach((button) => {
        button.classList.toggle('is-loading', isLoading);
        button.disabled = isLoading || this.totalQuantity() === 0;
        button.setAttribute('aria-busy', String(isLoading));
      });
    }

    showError(message) {
      this.querySelectorAll('[data-bcb-error]').forEach((element) => {
        element.hidden = !message;
        element.textContent = message || '';
      });
    }

    getSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    renderCartSections(state) {
      if (!state?.sections) return;

      this.getSectionsToRender().forEach((section) => {
        const targetRoot = document.getElementById(section.id);
        const sectionHtml = state.sections[section.section];
        if (!targetRoot || !sectionHtml) return;

        const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const source = parsedDocument.querySelector(section.selector);
        const target = targetRoot.querySelector(section.selector) || targetRoot;
        if (!source || !target) return;

        target.innerHTML = source.innerHTML;

        if (section.id === 'Cart-Drawer') {
          document.getElementById('Cart-Drawer')?.removeProductEvent?.();
        }
      });

      if (document.getElementById('Cart') && typeof Cart !== 'undefined') {
        new Cart().renderContents(state);
      }
    }

    openCartDrawer() {
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (typeof cartDrawer?.openDrawer === 'function') {
        cartDrawer.openDrawer();
      } else if (typeof cartDrawer?.open === 'function') {
        cartDrawer.open();
      } else if (window.theme?.routes?.cart_url) {
        window.location.href = window.theme.routes.cart_url;
      }
    }

    cartEndpoint(endpoint) {
      const root = window.Shopify?.routes?.root || '/';
      return endpoint === 'cart.js' ? `${root}cart.js` : `${root}cart/${endpoint}`;
    }

    jsonHeaders() {
      return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
    }

    formatMoney(cents) {
      const format = window.theme?.settings?.money_with_currency_format
        || window.theme?.settings?.money_format
        || ('$' + '{' + '{amount}' + '}');

      if (typeof window.formatMoney === 'function') {
        return window.formatMoney(Number(cents) || 0, format);
      }

      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format((Number(cents) || 0) / 100);
    }

    toNumber(value) {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    escapeHtml(value) {
      const element = document.createElement('div');
      element.textContent = value || '';
      return element.innerHTML;
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/`/g, '&#96;');
    }
  }

  customElements.define('bundle-collection-builder', BundleCollectionBuilder);
}
